import { MOBILE_BILLING_REASONS } from '../constants/mobileBilling';
import { dispatchMobileEvent, compactProperties } from './mobileAnalyticsClient';

// First-party mobile IAP/subscription funnel instrumentation.
//
// Built on the shared mobileAnalyticsClient dual-fire (Segment survives the
// Capacitor WebView where the third-party-tracking kill switch disables
// Mixpanel; Mixpanel stays a no-op on mobile for web parity). The client owns
// the "never throw into the app flow" guarantee. Segment here is our own
// product-analytics source (not IDFA / cross-app ad tracking), consistent with
// the shipped `NSPrivacyTracking=false` privacy manifest.

export const MOBILE_FUNNEL_EVENTS = {
  PAYWALL_VIEWED: 'Mobile Paywall Viewed',
  PURCHASE_INITIATED: 'Mobile Purchase Initiated',
  PURCHASE_CANCELLED: 'Mobile Purchase Cancelled',
  PURCHASE_FAILED: 'Mobile Purchase Failed',
  RESTORE_INITIATED: 'Mobile Restore Initiated',
  RESTORE_EMPTY: 'Mobile Restore Empty',
  RESTORE_FAILED: 'Mobile Restore Failed'
};

// Every mobile funnel event carries these so the funnel can be sliced by
// platform/provider without joining against another table.
const BASE_PROPERTIES = {
  platform: 'ios',
  provider: 'REVENUECAT'
};

export const buildMobileFunnelPayload = (properties = {}) => ({
  ...BASE_PROPERTIES,
  ...compactProperties(properties)
});

// Dual-fire a mobile funnel event (Segment + Mixpanel via the shared client).
// Returns true if at least one sink accepted the event; never throws.
export const trackMobileFunnelEvent = (event, properties = {}) => {
  if (!event) {
    return false;
  }

  return dispatchMobileEvent(event, buildMobileFunnelPayload(properties));
};

export const trackMobilePaywallViewed = (properties = {}) =>
  trackMobileFunnelEvent(MOBILE_FUNNEL_EVENTS.PAYWALL_VIEWED, properties);

export const trackMobilePurchaseInitiated = (properties = {}) =>
  trackMobileFunnelEvent(MOBILE_FUNNEL_EVENTS.PURCHASE_INITIATED, properties);

export const trackMobilePurchaseCancelled = (properties = {}) =>
  trackMobileFunnelEvent(MOBILE_FUNNEL_EVENTS.PURCHASE_CANCELLED, properties);

export const trackMobilePurchaseFailed = (properties = {}) =>
  trackMobileFunnelEvent(MOBILE_FUNNEL_EVENTS.PURCHASE_FAILED, properties);

export const trackMobileRestoreInitiated = (properties = {}) =>
  trackMobileFunnelEvent(MOBILE_FUNNEL_EVENTS.RESTORE_INITIATED, properties);

export const trackMobileRestoreEmpty = (properties = {}) =>
  trackMobileFunnelEvent(MOBILE_FUNNEL_EVENTS.RESTORE_EMPTY, properties);

export const trackMobileRestoreFailed = (properties = {}) =>
  trackMobileFunnelEvent(MOBILE_FUNNEL_EVENTS.RESTORE_FAILED, properties);

// Dev-paywall (local iOS preview) purchases/restores are intentionally
// disabled — their "failure" is not a real funnel signal and must not pollute
// the warehouse (the hard-coded Segment writeKey is shared with prod).
const isDevPaywallResult = (result) =>
  result?.mobileDevPaywall === true ||
  result?.reason === MOBILE_BILLING_REASONS.DEV_PAYWALL_STORE_DISABLED;

// Classify a purchase result into its terminal funnel event and fire it.
// Success (`purchased`) is intentionally NOT emitted here — the existing
// `Subscription bought` event already covers it. Returns the event name fired,
// or null when nothing should fire. Never throws.
export const trackMobilePurchaseOutcome = (result, properties = {}) => {
  if (!result || isDevPaywallResult(result)) {
    return null;
  }

  if (result.cancelled) {
    trackMobilePurchaseCancelled({ ...properties, stage: result.stage });
    return MOBILE_FUNNEL_EVENTS.PURCHASE_CANCELLED;
  }

  if (!result.purchased) {
    trackMobilePurchaseFailed({ ...properties, stage: result.stage, reason: result.reason });
    return MOBILE_FUNNEL_EVENTS.PURCHASE_FAILED;
  }

  return null;
};

// Classify a restore result into its terminal funnel event and fire it.
// Success (`restored`) is covered by the existing `Subscription restored`
// event. Returns the event name fired, or null. Never throws.
export const trackMobileRestoreOutcome = (result, properties = {}) => {
  if (!result || isDevPaywallResult(result)) {
    return null;
  }

  if (result.restored) {
    return null;
  }

  if (result.nothingToRestore || result.reason === MOBILE_BILLING_REASONS.NOTHING_TO_RESTORE) {
    trackMobileRestoreEmpty({ ...properties, stage: result.stage });
    return MOBILE_FUNNEL_EVENTS.RESTORE_EMPTY;
  }

  trackMobileRestoreFailed({ ...properties, stage: result.stage, reason: result.reason });
  return MOBILE_FUNNEL_EVENTS.RESTORE_FAILED;
};
